// User-made Twitch clips of the open VOD (КЛИПЫ tab) — pure helpers for mapping and merging.
// No Angular here; twitch-chat.service.ts fetches and wires them to signals,
// tools/test-user-clips.mjs tests this file.
import type { Highlight } from '../models/twitch.model';
import { uniqueStartTime } from './transcript.js';

/** The clip fields we use — a structural subset of Helix's clip, also built from GQL. */
export interface VodClip {
  id: string;
  url: string;
  title: string;
  creator_name: string;
  view_count: number;
  duration: number;   // seconds, 0.1 precision (Helix)
  vod_offset: number; // seconds
}

// Keeps the cloud-synced state doc (1MB cap) in check on clip-heavy VODs (~0.5 KB per clip).
export const MAX_VOD_CLIPS = 1000;

/**
 * GQL `Clip` node → VodClip, or null when it can't be placed on the timeline yet (a fresh
 * live clip has no offset for a few minutes). Helix reports `duration` truncated to 0.1 s
 * (GQL 30.02 → Helix 30, verified on 526 clips 2026-10-20) — truncate the same way so the
 * ceil in mapClipToHighlight keeps producing the startTimes that saved projects already have.
 */
export function vodClipFromGql(node: any): VodClip | null {
  if (!node?.slug || typeof node.videoOffsetSeconds !== 'number') return null;
  const rawDuration = node.duration ?? node.durationSeconds ?? 0;
  return {
    id: node.slug,
    url: node.url || `https://clips.twitch.tv/${node.slug}`,
    title: node.title || '',
    creator_name: node.curator?.displayName || '',
    view_count: node.viewCount ?? 0,
    duration: Math.floor(rawDuration * 10 + 1e-6) / 10,
    vod_offset: node.videoOffsetSeconds,
  };
}

/** Most-viewed first, at most MAX_VOD_CLIPS. */
export function capClipsByViews(clips: VodClip[]): VodClip[] {
  if (clips.length <= MAX_VOD_CLIPS) return clips;
  return [...clips].sort((a, b) => b.view_count - a.view_count).slice(0, MAX_VOD_CLIPS);
}

/**
 * Stable identity of a user-clip highlight: the clip slug (last path segment of its URL —
 * Helix gives clips.twitch.tv/<slug>, GQL twitch.tv/<login>/clip/<slug>). startTime can't
 * be the key: clips of the same moment share it, and mergeUserClips moves it to stay unique.
 */
export function userClipKey(h: Pick<Highlight, 'clipUrl' | 'startTime'>): string {
  const slug = h.clipUrl?.split(/[?#]/)[0].split('/').filter(Boolean).pop();
  return slug ? `clip:${slug}` : `t:${h.startTime}`;
}

const isTouched = (h: Highlight) =>
  !!(h.isPinned || h.isPromoted || h.isDeleted || h.isStartTimeManuallySet);

/**
 * Replace the user-clips in `current` with `fresh` (already mapped to highlights, startTime =
 * the clip's own start). Per clip (matched by slug) the user's pin / trash / promote / custom
 * borders carry over; a touched clip missing from `fresh` is kept (deleted on Twitch, or a
 * partial Helix fallback answer) — an untouched one goes. Duplicates in `fresh` (Helix pages
 * overlap) are dropped. Highlights are keyed by startTime across the app (select / pin /
 * delete), so each clip gets a unique one off the 15s peak grid via uniqueStartTime —
 * otherwise pinning one of two clips of the same moment pinned the other one. The clip
 * window (clipStartTime / clipEndTime) is untouched. Result sorted by startTime.
 */
export function mergeUserClips(current: Highlight[], fresh: Highlight[]): Highlight[] {
  const others = current.filter(h => h.source !== 'user-clip');
  const existing = current.filter(h => h.source === 'user-clip');
  const prevByKey = new Map(existing.map(h => [userClipKey(h), h]));

  const freshKeys = new Set<string>();
  const merged: Highlight[] = [];
  for (const c of fresh) {
    const key = userClipKey(c);
    if (freshKeys.has(key)) continue;
    freshKeys.add(key);
    const prev = prevByKey.get(key);
    merged.push(!prev ? c : {
      ...c,
      isPinned: prev.isPinned ?? false,
      isDeleted: prev.isDeleted ?? false,
      isPromoted: prev.isPromoted,
      clipStartTime: prev.clipStartTime ?? c.clipStartTime,
      clipEndTime: prev.clipEndTime ?? c.clipEndTime,
      isStartTimeManuallySet: prev.isStartTimeManuallySet ?? false,
    });
  }
  const kept = existing.filter(h => !freshKeys.has(userClipKey(h)) && isTouched(h));

  const taken = new Set(others.map(h => h.startTime));
  const placed = [...merged, ...kept]
    .sort((a, b) => (a.startTime - b.startTime) || userClipKey(a).localeCompare(userClipKey(b)))
    .map(h => {
      const startTime = uniqueStartTime(h.startTime, taken);
      taken.add(startTime);
      return startTime === h.startTime ? h : { ...h, startTime };
    });
  return [...others, ...placed].sort((a, b) => a.startTime - b.startTime);
}
